// src/pages/CustomerDashboard.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { SERVICES } from '../data/mockData';
import {
  postJob,
  listenCustomerJobs,
  listenToBids,
  listenToJobWithPrivate,
  acceptBid,
  getAcceptedBidForJob,
  updateJobStatus,
  createWasteTransferNote,
  getWasteTransferNotes,
  submitReview,
  hasExistingReview,
  getProviderReviewCount,
  getLeaderboard,
  cancelJob,
  uploadJobImages,
  updateJobImageUrls,
  expireJobReferrals,
} from '../services/firestoreService';
import { licenceForPaperwork } from '../utils/jobPrivacy';
import DutyCareNotice from '../components/DutyCareNotice';
import RegisterCheck from '../components/RegisterCheck';
import VerifiedBadge from '../components/VerifiedBadge';
import WasteTransferNote from '../components/WasteTransferNote';
import LevelBadge from '../components/LevelBadge';
import BadgeShelf from '../components/BadgeShelf';
import JobChat from '../components/JobChat';
import PayJobButton from '../components/PayJobButton';
import '../pages/CustomerDashboard.css';

const STEPS = ['Post Job', 'View Bids', 'Track Job', 'Complete'];

// ── Payment gating ───────────────────────────────────────────────────────────
// Stripe pay was re-enabled in 940007c (7 Oct 2026). Jobs created before then are
// legacy and never require payment. createPaymentIntent enforces a £20 minimum, so
// jobs below that are not gated either (they could never be paid).
const PAYMENTS_LIVE_FROM = new Date('2026-10-07T11:00:00Z');
const MIN_PAYABLE_GBP = 20;
const parsePrice = (v) => {
  const n = parseFloat(String(v ?? '').replace(/[^0-9.]/g, ''));
  return isNaN(n) ? null : n;
};
const jobCreatedAt = (job) => {
  const c = job?.createdAt;
  if (!c) return null;
  return typeof c.toDate === 'function' ? c.toDate() : new Date(c);
};
// Free = scrap collection or a £0 job/bid — no payment ever shown or required.
const isFreeJob = (job, bid) => {
  if (!job) return false;
  if (job.serviceType === 'Scrap Collection') return true;
  return parsePrice(bid?.price ?? job.price) === 0;
};
const requiresPayment = (job, bid) => {
  if (!job || isFreeJob(job, bid)) return false;
  const created = jobCreatedAt(job);
  if (created && created < PAYMENTS_LIVE_FROM) return false;
  const price = parsePrice(bid?.price);
  if (price !== null && price < MIN_PAYABLE_GBP) return false;
  return true;
};
const isAwaitingPayment = (job, bid) => requiresPayment(job, bid) && job?.paymentStatus !== 'paid';

// Reusable photo strip — shown on job cards/summaries when images exist
const PhotoStrip = ({ urls }) => {
  const [lightbox, setLightbox] = React.useState(null);
  if (!urls?.length) return null;
  return (
    <>
      <div className="photo-strip">
        {urls.map((url, i) => (
          <img
            key={i}
            src={url}
            alt={`View ${i + 1} of ${urls.length}`}
            className="photo-strip-thumb"
            onClick={() => setLightbox(url)}
          />
        ))}
      </div>
      {lightbox && (
        <div className="lightbox-overlay" onClick={() => setLightbox(null)}>
          <button className="lightbox-close" onClick={() => setLightbox(null)}>✕</button>
          <img src={lightbox} alt="Full size" className="lightbox-img" onClick={e => e.stopPropagation()} />
        </div>
      )}
    </>
  );
};

const CustomerDashboard = () => {
  const { user, refreshUser } = useAuth();
  const navigate  = useNavigate();

  const [step, setStep]             = useState(0);
  const [form, setForm]             = useState({ serviceType:'', pickup:'', pickupAddress:'', dropoff:'', dropoffAddress:'', description:'', date:'', budget:'' });
  const [myJobs, setMyJobs]         = useState([]);
  const [activeJob, setActiveJob]   = useState(null);
  const [bids, setBids]             = useState([]);
  const [acceptedBid, setAcceptedBid] = useState(null);
  const [posting, setPosting]       = useState(false);
  const [loadingJobs, setLoadingJobs] = useState(true);
  const [wtnData, setWtnData]       = useState(null);
  const [starRating, setStarRating] = useState(0);
  const [starHover, setStarHover]   = useState(0);
  const [reviewText, setReviewText] = useState('');
  const [reviewSubmitted, setReviewSubmitted] = useState(false);
  const [reviewRedirectCount, setReviewRedirectCount] = useState(3);
  const [submittingReview, setSubmittingReview] = useState(false);
  const [jobMarkedComplete, setJobMarkedComplete] = useState(false);
  const [toast, setToast] = useState('');
  const [liveJobStatus, setLiveJobStatus] = useState('Accepted');
  const [leaderboard, setLeaderboard] = useState([]);
  const [cancelConfirm, setCancelConfirm] = useState(false);
  const [showHistory, setShowHistory]     = useState(false);
  const [wtns, setWtns]                   = useState([]);
  const [wtnsLoaded, setWtnsLoaded]       = useState(false);
  const [wtnsLoading, setWtnsLoading]     = useState(false);
  const [historyWtnModal, setHistoryWtnModal] = useState(null);
  const [providerReviewCounts, setProviderReviewCounts] = useState({});

  // Image upload state
  const [imageFiles, setImageFiles]         = useState([]);   // compressed Blobs
  const [imagePreviews, setImagePreviews]   = useState([]);   // data URLs for preview
  const [uploadProgress, setUploadProgress] = useState(null); // 0–100 or null
  const fileInputRef    = useRef(null);
  const hasRestoredRef  = useRef(false); // gate step-restoration to first snapshot only

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3500); };

  // Compress image to ≤1 MB using canvas — returns a Blob with a .name property
  const compressImage = useCallback((file) => new Promise((resolve) => {
    const MAX_BYTES = 1024 * 1024; // 1 MB
    const MAX_DIM   = 1920;
    const img       = new Image();
    const objectUrl = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      let { width, height } = img;
      const ratio = Math.min(MAX_DIM / width, MAX_DIM / height, 1);
      width  = Math.round(width  * ratio);
      height = Math.round(height * ratio);
      const canvas = document.createElement('canvas');
      canvas.width  = width;
      canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);
      const tryCompress = (quality) => {
        canvas.toBlob((blob) => {
          if (!blob) { resolve(file); return; }
          if (blob.size <= MAX_BYTES || quality <= 0.3) {
            // Attach original filename so uploadJobImages can name the storage object
            Object.defineProperty(blob, 'name', { value: file.name, writable: false });
            resolve(blob);
          } else {
            tryCompress(Math.round((quality - 0.1) * 10) / 10);
          }
        }, 'image/jpeg', quality);
      };
      tryCompress(0.85);
    };
    img.src = objectUrl;
  }), []);

  useEffect(() => { if (!user) navigate('/login'); }, [user, navigate]);
  useEffect(() => { document.title = 'My Jobs — The WasteGame'; }, []);

  // Load leaderboard once on mount (one-time fetch — rank changes are not urgent enough
  // to justify the cost of a full users-collection onSnapshot listener).
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    getLeaderboard(5).then(lb => { if (!cancelled) setLeaderboard(lb); });
    return () => { cancelled = true; };
  }, [user?.uid]); // eslint-disable-line react-hooks/exhaustive-deps

  // Real-time listener for customer's jobs — keeps job history and statuses live.
  // Step-restoration (which screen to show on load) runs only on the FIRST snapshot
  // via hasRestoredRef so subsequent Firestore updates don't reset the active step.
  useEffect(() => {
    if (!user) return;
    hasRestoredRef.current = false; // reset on uid change (login/logout)
    let cancelled = false;
    const unsub = listenCustomerJobs(user.uid, async (jobs) => {
      if (cancelled) return;
      setMyJobs(jobs);
      setLoadingJobs(false);

      if (hasRestoredRef.current) return; // subsequent snapshots only update the list
      hasRestoredRef.current = true;

      const pendingJob = jobs.find(j => j.status === 'Pending');
      if (pendingJob) {
        setActiveJob(pendingJob);
        setStep(1);
        return;
      }

      // Restore tracking screen for in-flight jobs
      const activeStatuses = ['Accepted', 'En Route', 'Arrived'];
      const inFlightJob = jobs.find(j => activeStatuses.includes(j.status));
      if (inFlightJob) {
        setActiveJob(inFlightJob);
        setLiveJobStatus(inFlightJob.status);
        const bid = await getAcceptedBidForJob(inFlightJob.id);
        if (cancelled) return;
        if (bid) setAcceptedBid(bid);
        setStep(2);
        return;
      }

      // Completed jobs: reopen step 3 for the newest completed job if it is unreviewed,
      // or for any completed job that still needs payment (priced, unpaid, post-launch).
      // Only skip when reviewed AND (paid or free).
      const completedJobs = jobs.filter(j => j.status === 'Completed');
      const candidates = completedJobs.filter((j, i) => i === 0 || isAwaitingPayment(j, null));
      for (const completedJob of candidates) {
        const alreadyReviewed = await hasExistingReview(completedJob.id, user.uid);
        if (cancelled) return;
        const bid = await getAcceptedBidForJob(completedJob.id);
        if (cancelled) return;
        if (alreadyReviewed && !isAwaitingPayment(completedJob, bid)) continue; // all done
        setActiveJob(completedJob);
        if (bid) setAcceptedBid(bid);
        setJobMarkedComplete(true);
        setStep(3);
        return;
      }
    });
    return () => { cancelled = true; unsub(); };
  }, [user?.uid]); // eslint-disable-line react-hooks/exhaustive-deps

  // Listen to bids in real-time when we have an active job
  useEffect(() => {
    if (!activeJob?.id) return;
    const unsub = listenToBids(activeJob.id, setBids);
    return unsub;
  }, [activeJob?.id]);

  // Fetch review counts for providers who've bid — keyed by providerId
  useEffect(() => {
    const unresolved = bids.filter(b => b.providerId && !(b.providerId in providerReviewCounts));
    if (!unresolved.length) return;
    unresolved.forEach(bid => {
      getProviderReviewCount(bid.providerId).then(count => {
        setProviderReviewCounts(prev => ({ ...prev, [bid.providerId]: count }));
      });
    });
  }, [bids]); // eslint-disable-line react-hooks/exhaustive-deps

  // Listen to live job status and the private address. Syncs activeJob so PayJobButton
  // and canComplete react to Firestore changes. Auto-advances from tracking (step 2) to
  // complete (step 3) when the carrier marks the job Completed.
  useEffect(() => {
    if (!activeJob?.id) return;
    const unsub = listenToJobWithPrivate(activeJob.id, (job) => {
      if (step === 2 || step === 3) setLiveJobStatus(job.status);
      setActiveJob(prev => (prev && prev.id === job.id ? { ...prev, ...job } : prev));
      if (step === 2 && job.status === 'Completed') {
        setJobMarkedComplete(true);
        setStep(3);
      }
    });
    return unsub;
  }, [step, activeJob?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-redirect to fresh Post Job screen 3 seconds after review is submitted
  useEffect(() => {
    if (!reviewSubmitted) return;
    // Never navigate away while a priced job is still unpaid
    if (isAwaitingPayment(activeJob, acceptedBid)) return;
    setReviewRedirectCount(3);
    const tick = setInterval(() => {
      setReviewRedirectCount(n => {
        if (n <= 1) {
          clearInterval(tick);
          handleReset();
          return 0;
        }
        return n - 1;
      });
    }, 1000);
    return () => clearInterval(tick);
  }, [reviewSubmitted, activeJob?.paymentStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = e => setForm({ ...form, [e.target.name]: e.target.value });

  const handleImagePick = async (e) => {
    const picked = Array.from(e.target.files || []);
    if (!picked.length) return;
    const remaining = 4 - imageFiles.length;
    const toAdd     = picked.slice(0, remaining);
    // Build previews immediately from raw files for instant UI feedback
    const previews = toAdd.map(f => URL.createObjectURL(f));
    // Compress in background — swap preview with blob once done
    const blobs = await Promise.all(toAdd.map(f => compressImage(f)));
    setImageFiles(prev => [...prev, ...blobs]);
    setImagePreviews(prev => [...prev, ...previews]);
    // Reset input so the same file can be re-selected if needed
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleRemoveImage = (idx) => {
    URL.revokeObjectURL(imagePreviews[idx]);
    setImageFiles(prev    => prev.filter((_, i) => i !== idx));
    setImagePreviews(prev => prev.filter((_, i) => i !== idx));
  };

  const handlePost = async (e) => {
    e.preventDefault();
    if (!form.serviceType)     { showToast('Please select a service type.'); return; }
    if (!form.pickup)          { showToast('Please enter a pickup postcode.'); return; }
    if (!form.pickupAddress)   { showToast('Please enter the full pickup address.'); return; }
    if (!form.description)     { showToast('Please describe the job.'); return; }
    if (!user.emailVerified) {
      showToast('Please verify your email before posting a job. Check your inbox.');
      return;
    }
    setPosting(true);

    // ── Step 1: Create the job document ──────────────────────────────────────
    let jobId;
    try {
      jobId = await postJob({ ...form, customerName: user.name, customerPhoto: user.profilePhoto || '' }, user.uid);
      console.log('[handlePost] Job created — jobId:', jobId);
    } catch (err) {
      console.error('[handlePost] postJob failed:', err.code, err.message, err);
      const msg = err.code === 'permission-denied'
        ? 'Please verify your email before posting a job.'
        : "We couldn't post your job. Please try again.";
      showToast(msg);
      setPosting(false);
      return;
    }

    // ── Step 2: Upload images (optional — job already exists regardless) ──────
    let imageUrls = [];
    if (imageFiles.length > 0) {
      setUploadProgress(0);
      try {
        imageUrls = await uploadJobImages(jobId, imageFiles, setUploadProgress);
        await updateJobImageUrls(jobId, imageUrls);
        console.log('[handlePost] Images uploaded:', imageUrls);
      } catch (err) {
        console.error('[handlePost] Image upload failed:', err.code, err.message, err);
        showToast("One of your photos couldn't be uploaded. You can still post the job without it.");
        // Still proceed to Step 1; job was created without images
      }
      setUploadProgress(null);
      imagePreviews.forEach(u => URL.revokeObjectURL(u));
      setImageFiles([]);
      setImagePreviews([]);
    }

    // Do NOT optimistically push to myJobs here — listenCustomerJobs will fire
    // with the real Firestore doc within milliseconds. Prepending here races with
    // that snapshot and produces a duplicate entry (same ID appears twice).
    const newJob = { id: jobId, ...form, imageUrls, status: 'Pending', customerId: user.uid };
    setActiveJob(newJob);
    setStep(1);
    setPosting(false);
  };

  const handleAccept = async (bid) => {
    try {
      await acceptBid(bid.id, activeJob.id, bid.providerId);
      setAcceptedBid(bid);
      setStep(2);
    } catch (err) {
      console.error('[handleAccept] failed:', err.code, err.message, err);
      showToast(`Failed to accept bid: ${err.message || err.code || 'Unknown error'}`);
    }
  };

  const handleCancelJob = async () => {
    try {
      await cancelJob(activeJob.id);
      expireJobReferrals(activeJob.id).catch(() => {}); // expire any referrals silently
      setMyJobs(prev => prev.map(j => j.id === activeJob.id ? { ...j, status: 'Cancelled' } : j));
      setCancelConfirm(false);
      setActiveJob(null);
      setBids([]);
      setStep(0);
    } catch (err) {
      console.error('Error cancelling job:', err);
    }
  };

  const handleComplete = async () => {
    try {
      await updateJobStatus(activeJob.id, 'Completed');
      // Update activeJob in state so PayJobButton sees status: 'Completed' immediately
      setActiveJob(prev => ({ ...prev, status: 'Completed' }));
      if (acceptedBid && !wtnData) {  // guard prevents duplicate WTN if called more than once
        const wtn = await createWasteTransferNote(
          activeJob.id,
          acceptedBid.providerId,
          user.uid,
          { serviceType: activeJob.serviceType, location: activeJob.pickup || activeJob.pickupArea || '', pickupAddress: activeJob.pickupAddress || '', dropoffAddress: activeJob.dropoffAddress || '', dropoff: activeJob.dropoff || '', description: activeJob.description, carrierName: acceptedBid.providerName, licenceNo: licenceForPaperwork(acceptedBid.licenceNo), ecoRate: acceptedBid.ecoRate, price: acceptedBid.price }
        );
        setWtnData({ ...activeJob, wtnRef: wtn });
      }
      setJobMarkedComplete(true);
      setStep(3);
    } catch (err) {
      console.error('Error completing job:', err);
    }
  };

  const handleSubmitReview = async () => {
    if (!starRating || !acceptedBid) return;
    setSubmittingReview(true);
    try {
      await submitReview({
        jobId:      activeJob.id,
        customerId: user.uid,
        providerId: acceptedBid.providerId,
        rating:     starRating,
        review:     reviewText,
      });
      setReviewSubmitted(true);
      showToast('Review submitted — thank you!');
      refreshUser();
    } catch (err) {
      alert(`Failed to submit review: ${err.message}`);
    }
    setSubmittingReview(false);
  };

  const handleReset = () => {
    setStep(0);
    setForm({ serviceType:'', pickup:'', pickupAddress:'', dropoff:'', dropoffAddress:'', description:'', date:'', budget:'' });
    setActiveJob(null);
    setBids([]);
    setAcceptedBid(null);
    setStarRating(0);
    setStarHover(0);
    setReviewText('');
    setReviewSubmitted(false);
    setJobMarkedComplete(false);
    imagePreviews.forEach(u => URL.revokeObjectURL(u));
    setImageFiles([]);
    setImagePreviews([]);
    setUploadProgress(null);
  };

  const handleOpenHistory = async () => {
    setShowHistory(true);
    if (!wtnsLoaded) {
      setWtnsLoading(true);
      const notes = await getWasteTransferNotes(user.uid);
      setWtns(notes);
      setWtnsLoaded(true);
      setWtnsLoading(false);
    }
  };

  const wtnByJobId = wtns.reduce((acc, w) => { acc[w.jobId] = w; return acc; }, {});

  const jobStatusBadge = (status) => {
    if (status === 'Completed') return 'badge-teal';
    if (status === 'Cancelled') return 'badge-muted';
    if (status === 'Pending')   return 'badge-amber';
    return 'badge-fire'; // Accepted / En Route / Arrived
  };

  if (!user) return null;

  if (user.type !== 'customer') {
    return (
      <div className="dashboard-page">
        <div className="page-wrapper" style={{ paddingTop: '48px', paddingBottom: '48px' }}>
          <div className="card" style={{ maxWidth: '560px', margin: '0 auto', borderLeft: '4px solid var(--teal)', padding: '32px' }}>
            <h2 className="card-title" style={{ marginBottom: '12px' }}>CUSTOMER <span>ACCOUNTS ONLY</span></h2>
            <p style={{ color: 'var(--text-muted)', marginBottom: '20px', lineHeight: '1.6' }}>
              Only customer accounts can post jobs. If you're a carrier, you can find work by browsing
              available jobs and bidding on them. To post jobs, please sign up as a customer.
            </p>
            <Link to="/provider-dashboard" className="btn btn-primary">Go to Carrier Dashboard</Link>
          </div>
        </div>
      </div>
    );
  }

  // Review is unlocked once the customer has marked the job complete AND, for priced
  // jobs, payment has gone through (paymentStatus is synced live by listenToJobWithPrivate).
  const awaitingPayment = isAwaitingPayment(activeJob, acceptedBid);
  const reviewEnabled = jobMarkedComplete && !awaitingPayment;

  return (
    <div className="dashboard-page">
      {toast && <div className="toast">{toast}</div>}
      <div className="page-wrapper">

        <div className="dash-header">
          <div>
            <h1 className="section-title">CUSTOMER <span>DASHBOARD</span></h1>
            <div style={{display:'flex', alignItems:'center', gap:'10px', flexWrap:'wrap', margin:'4px 0 8px'}}>
              {user.profilePhoto
                ? <img src={user.profilePhoto} alt={user.name} className="cd-user-avatar-img" />
                : <div className="cd-user-avatar-initials">{user.name?.charAt(0) || '?'}</div>
              }
              <p className="text-muted" style={{margin:0}}>Welcome back, {user.name}</p>
              <Link to="/customer-profile/edit" className="btn btn-outline btn-sm">✏️ Edit Profile</Link>
            </div>
            <button
              className="btn-history-toggle"
              onClick={showHistory ? () => setShowHistory(false) : handleOpenHistory}
            >
              {showHistory ? '← Back to Dashboard' : `📋 Job History (${myJobs.length})`}
            </button>
          </div>
          <div className="dash-step-pills">
            {STEPS.map((s, i) => (
              <div key={i} className={`step-pill ${step === i ? 'active' : ''} ${step > i ? 'done' : ''}`}>
                <span className="step-num">{step > i ? '✓' : i + 1}</span>{s}
              </div>
            ))}
          </div>
        </div>

        {/* JOB HISTORY PANEL */}
        {showHistory && (
          <div className="job-history-panel">
            <h2 className="card-title" style={{marginBottom:'16px'}}>JOB <span>HISTORY</span></h2>
            {wtnsLoading && <p className="text-muted">Loading history...</p>}
            {!wtnsLoading && myJobs.length === 0 && (
              <div className="card" style={{textAlign:'center',padding:'30px'}}>
                <p className="text-muted">No jobs yet — post your first job to get started.</p>
              </div>
            )}
            {myJobs.map(job => {
              const wtn = wtnByJobId[job.id];
              const isCancelled = job.status === 'Cancelled';
              const isCompleted = job.status === 'Completed';
              const isActive    = ['Accepted','En Route','Arrived'].includes(job.status);
              const carrierName = wtn?.carrierName
                || (isActive    ? 'Carrier assigned'   : null)
                || (job.status === 'Pending' ? 'Awaiting bids' : null)
                || (isCancelled ? null : null);
              const createdDate = job.createdAt?.toDate
                ? job.createdAt.toDate().toLocaleDateString('en-GB', { day:'numeric', month:'short', year:'numeric' })
                : job.date || '—';
              return (
                <div key={job.id} className={`jh-card ${isCancelled ? 'jh-cancelled' : ''}`}>
                  <div className="jh-top">
                    <div className="jh-title-row">
                      <span className="jh-icon">{job.icon || '🗑️'}</span>
                      <div>
                        <div className="jh-type">{job.serviceType || 'Job'}</div>
                        <div className="jh-loc">📍 {job.pickupAddress || job.pickup || job.pickupArea || '—'}</div>
                      </div>
                    </div>
                    <span className={`badge ${jobStatusBadge(job.status)}`}>{job.status}</span>
                  </div>
                  <div className="jh-meta">
                    <span>📅 Posted {createdDate}</span>
                    {carrierName && <span>👷 {carrierName}</span>}
                    {isCompleted && wtn?.price && <span className="jh-price">💰 £{wtn.price}</span>}
                  </div>
                  {job.description && (
                    <div className="jh-desc">{job.description}</div>
                  )}
                  <div className="jh-actions">
                    {isCompleted && wtn && (
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => setHistoryWtnModal({ job, wtn })}
                      >
                        📋 View WTN
                      </button>
                    )}
                    {isCompleted && !wtn && (
                      <span className="jh-no-wtn text-muted">WTN not available</span>
                    )}
                    {isActive && (
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={() => setShowHistory(false)}
                      >
                        Track Live →
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className={`dash-grid${showHistory ? ' dash-grid-hidden' : ''}`}>
          <div className="dash-main">

            {/* STEP 0: POST JOB */}
            {step === 0 && (
              <div className="card">
                <h2 className="card-title">POST A <span>JOB</span></h2>
                <DutyCareNotice variant="info" />
                <div className="service-type-grid">
                  {SERVICES.map(s => (
                    <button key={s.id}
                      type="button"
                      className={`svc-type-btn ${form.serviceType === s.name ? 'selected' : ''}`}
                      onClick={() => setForm({ ...form, serviceType: s.name })}
                    >
                      <span className="svc-type-icon">{s.icon}</span>
                      <span className="svc-type-name">{s.name}</span>
                    </button>
                  ))}
                </div>
                <form onSubmit={handlePost}>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Pickup Postcode *</label>
                      <input className="form-input" name="pickup" placeholder="e.g. AB12 3CD" value={form.pickup} onChange={handleChange} required />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Drop-off Postcode (if needed)</label>
                      <input className="form-input" name="dropoff" placeholder="e.g. CF10 2NA" value={form.dropoff} onChange={handleChange} />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Full Pickup Address *</label>
                      <input className="form-input" name="pickupAddress" placeholder="e.g. 14 High Street, Swansea" value={form.pickupAddress} onChange={handleChange} required />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Full Drop-off Address</label>
                      <input className="form-input" name="dropoffAddress" placeholder="e.g. Swansea Tip, Llansamlet" value={form.dropoffAddress} onChange={handleChange} />
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Describe the Job *</label>
                    <input className="form-input" name="description" placeholder="e.g. 3 black bags, old sofa, broken TV" value={form.description} onChange={handleChange} required />
                  </div>

                  {/* IMAGE UPLOAD */}
                  <div className="form-group">
                    <label className="form-label">Photos <span className="form-label-hint">(optional · up to 4)</span></label>
                    <div className="img-upload-area">
                      {imagePreviews.map((src, idx) => (
                        <div key={idx} className="img-thumb-wrap">
                          <img src={src} alt={`Preview ${idx + 1}`} className="img-thumb" />
                          <button type="button" className="img-thumb-remove" onClick={() => handleRemoveImage(idx)} aria-label="Remove image">✕</button>
                        </div>
                      ))}
                      {imageFiles.length < 4 && (
                        <button type="button" className="img-add-btn" onClick={() => fileInputRef.current?.click()}>
                          <span className="img-add-icon">📷</span>
                          <span className="img-add-label">Add photo</span>
                        </button>
                      )}
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      multiple
                      style={{ display: 'none' }}
                      onChange={handleImagePick}
                    />
                    {uploadProgress !== null && (
                      <div className="img-upload-progress">
                        <div className="img-upload-bar" style={{ width: `${uploadProgress}%` }} />
                        <span className="img-upload-label">Uploading photos… {uploadProgress}%</span>
                      </div>
                    )}
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Preferred collection date *</label>
                      <input className="form-input" type="date" name="date" value={form.date} onChange={handleChange} required />
                    </div>
                    {form.serviceType !== 'Scrap Collection' && (
                      <div className="form-group">
                        <label className="form-label">Budget</label>
                        <div className="input-prefix-wrap">
                          <span className="input-prefix">£</span>
                          <input className="form-input" name="budget" placeholder="e.g. 40–60" value={form.budget} onChange={handleChange} />
                        </div>
                      </div>
                    )}
                  </div>
                  {form.serviceType === 'Scrap Collection' && (
                    <p style={{fontSize:'0.85rem',color:'var(--teal)',background:'var(--teal-dim)',padding:'10px 14px',borderRadius:'var(--radius-sm)',border:'1px solid rgba(62,207,160,0.25)',marginBottom:'12px'}}>
                      ℹ️ Scrap collection is free — carriers collect your scrap as their payment.
                    </p>
                  )}
                  {form.serviceType !== 'Scrap Collection' && (
                    <div className="verified-assurance">
                      Waste carriers are checked against the public register before they&apos;re approved. <RegisterCheck />
                    </div>
                  )}
                  <button type="submit" className="btn btn-primary btn-full" disabled={posting}>
                    {posting ? 'POSTING...' : 'POST JOB — GET QUOTES'}
                  </button>
                </form>
              </div>
            )}

            {/* STEP 1: BIDS */}
            {step === 1 && (
              <div className="card">
                <div className="bids-header">
                  <h2 className="card-title">CARRIER <span>BIDS</span></h2>
                  <div className="live-pill"><div className="live-dot" />{bids.length} bids</div>
                </div>
                <div className="job-summary">
                  <span className="job-summary-type">{activeJob?.serviceType}</span>
                  <span>{activeJob?.pickup}</span>
                  <span>{activeJob?.description}</span>
                </div>
                {activeJob?.imageUrls?.length > 0 && (
                  <PhotoStrip urls={activeJob.imageUrls} />
                )}
                {bids.length === 0 ? (
                  <div className="waiting-bids">
                    <div className="wb-spinner" />
                    <p>Waiting for carriers to bid on your job...<br /><span style={{fontSize:'0.78rem',color:'var(--muted)'}}>A price is not promised.</span></p>
                  </div>
                ) : (
                  <>
                    {activeJob?.serviceType !== 'Scrap Collection' && (
                      <div className="bids-verified-note">
                        Waste carriers are checked against the public register before they&apos;re approved. <RegisterCheck />
                      </div>
                    )}
                    {bids.map((bid, i) => (
                      <div key={bid.id} className={`bid-card ${i === 0 ? 'bid-top' : ''}`}>
                        {i === 0 && <div className="bid-top-label">🏆 Top Carrier</div>}
                        <Link to={`/carrier/${bid.providerId}`} className="bid-avatar" style={{textDecoration:'none',color:'inherit'}}>
                          {bid.profilePhoto
                            ? <img src={bid.profilePhoto} alt={bid.providerName} style={{width:'100%',height:'100%',borderRadius:'50%',objectFit:'cover'}} />
                            : (bid.initials || bid.providerName?.charAt(0))
                          }
                        </Link>
                        <div className="bid-info">
                          <div className="bid-name">
                            <Link to={`/carrier/${bid.providerId}`} style={{color:'inherit',textDecoration:'none'}}>{bid.providerName}</Link>
                            <LevelBadge jobs={bid.jobs || 0} size="sm" />
                            {/* phoneNumber not shown pre-acceptance — only visible to customer after bid is accepted */}
                          </div>
                          {licenceForPaperwork(bid.licenceNo) && <VerifiedBadge licenceNo={licenceForPaperwork(bid.licenceNo)} />}
                          <div className="bid-meta" style={{marginTop:'5px'}}>
                            <span className="stars">{'★'.repeat(Math.round(bid.rating || 0))}</span>
                            <span>
                              {bid.rating || 'New'}
                              {providerReviewCounts[bid.providerId] != null
                                ? ` · ${providerReviewCounts[bid.providerId]} review${providerReviewCounts[bid.providerId] !== 1 ? 's' : ''}`
                                : ''}
                              {' · '}{bid.jobs || 0} jobs
                            </span>
                          </div>
                          <BadgeShelf provider={bid} compact />
                          {bid.note && <div style={{fontSize:'0.8rem',color:'var(--muted)',marginTop:'5px'}}>"{bid.note}"</div>}
                        </div>
                        <div className="bid-right">
                          <div className="bid-price">£{bid.price}</div>
                          <div className="bid-eta">ETA {bid.eta}</div>
                          <button className="btn btn-primary btn-sm" onClick={() => handleAccept(bid)}>ACCEPT</button>
                        </div>
                      </div>
                    ))}
                  </>
                )}
                <button className="btn btn-secondary btn-full" style={{marginTop:'12px'}} onClick={() => setStep(0)}>← Edit Job</button>
                {!cancelConfirm ? (
                  <button className="btn btn-cancel btn-full" style={{marginTop:'8px'}} onClick={() => setCancelConfirm(true)}>
                    ✕ Cancel Job
                  </button>
                ) : (
                  <div className="cancel-confirm">
                    <p>Are you sure you want to cancel this job?</p>
                    <div className="cancel-confirm-btns">
                      <button className="btn btn-cancel btn-sm" onClick={handleCancelJob}>Yes, Cancel</button>
                      <button className="btn btn-secondary btn-sm" onClick={() => setCancelConfirm(false)}>Keep Job</button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* STEP 2: TRACKING */}
            {step === 2 && (() => {
              const STATUS_ORDER = ['Accepted', 'En Route', 'Arrived', 'Completed'];
              const statusIndex  = STATUS_ORDER.indexOf(liveJobStatus);
              // One step per status — each maps to its own index
              const trackSteps = [
                { label: `Bid accepted — ${acceptedBid?.providerName || 'carrier'} confirmed` },
                { label: 'Carrier is on the way' },
                { label: 'Carrier has arrived at your location' },
                { label: 'Collection complete — waste transfer note saved' },
              ];
              const badgeLabel = { 'Accepted': 'ACCEPTED', 'En Route': 'EN ROUTE', 'Arrived': 'ARRIVED', 'Completed': 'COMPLETE' }[liveJobStatus] || 'ACCEPTED';
              return (
                <div className="card">
                  <h2 className="card-title">LIVE <span>TRACKING</span></h2>
                  <div className="track-map">
                    <div className="track-map-grid" />
                    <div className="track-van">🚐</div>
                    <div className="track-pin">📍</div>
                    <div className="track-badge">{badgeLabel}</div>
                  </div>
                  <div className="track-steps">
                    {trackSteps.map((s, i) => {
                      const done   = statusIndex > i;
                      const active = statusIndex === i;
                      return (
                        <div key={i} className="track-step-row">
                          <div className={`track-dot ${done ? 'done' : active ? 'active' : 'pending'}`} />
                          <span className={`track-label ${done ? 'done' : active ? 'active' : 'pending'}`}>{s.label}</span>
                        </div>
                      );
                    })}
                  </div>
                  {activeJob && (
                    <div className="ajp-job-details" style={{marginTop:'14px'}}>
                      <span>📍 {activeJob.pickupAddress || activeJob.pickup || activeJob.pickupArea || 'Address unavailable'}</span>
                      {(activeJob.dropoffAddress || activeJob.dropoff || activeJob.dropoffArea) && (
                        <span>🏁 {activeJob.dropoffAddress || activeJob.dropoff || activeJob.dropoffArea}</span>
                      )}
                    </div>
                  )}
                  {acceptedBid && (
                    <div className="accepted-carrier">
                      <div className="bid-avatar">
                        {acceptedBid.profilePhoto
                          ? <img src={acceptedBid.profilePhoto} alt={acceptedBid.providerName} style={{width:'100%',height:'100%',borderRadius:'50%',objectFit:'cover'}} />
                          : (acceptedBid.initials || acceptedBid.providerName?.charAt(0))
                        }
                      </div>
                      <div className="bid-info">
                        <div className="bid-name">{acceptedBid.providerName}</div>
                        {licenceForPaperwork(acceptedBid.licenceNo) && <VerifiedBadge licenceNo={licenceForPaperwork(acceptedBid.licenceNo)} />}
                      </div>
                      {acceptedBid?.phoneNumber && (
                        <a href={`tel:${acceptedBid.phoneNumber}`} className="btn-call btn-call-lg">
                          📞 <span className="call-number">{acceptedBid.phoneNumber}</span>
                        </a>
                      )}
                    </div>
                  )}
                  <button className="btn btn-primary btn-full" style={{marginTop:'14px'}} onClick={handleComplete}>
                    MARK JOB AS COMPLETE
                  </button>
                </div>
              );
            })()}

            {/* CHAT — Step 2 only */}
            {step === 2 && activeJob?.id && acceptedBid?.providerId && (
              <div className="card" style={{marginTop:'16px'}}>
                <h2 className="card-title" style={{marginBottom:'12px'}}>MESSAGE YOUR <span>CARRIER</span></h2>
                <JobChat
                  jobId={activeJob.id}
                  currentUser={user}
                  otherUserId={acceptedBid.providerId}
                />
              </div>
            )}

            {/* STEP 3: COMPLETE */}
            {step === 3 && (
              <div className="card">
                <div className="job-complete-header">
                  <div className="jc-tick">✅</div>
                  <div>
                    <h2 className="card-title" style={{marginBottom:'4px'}}>JOB <span>COMPLETE!</span></h2>
                    <p className="text-muted">Your waste transfer note has been saved to your account.</p>
                  </div>
                </div>
                <PayJobButton job={activeJob} acceptedBid={acceptedBid} />

                <WasteTransferNote job={wtnData || activeJob} carrier={acceptedBid} wtnRef={wtnData?.wtnRef} />

                {/* MARK JOB AS COMPLETE — hidden once confirmed */}
                {!jobMarkedComplete && (
                  <div style={{marginTop:'16px'}}>
                    <button
                      className="btn btn-primary btn-full"
                      onClick={handleComplete}
                    >
                      ✅ MARK JOB AS COMPLETE
                    </button>
                  </div>
                )}

                <div className="rate-carrier" style={{marginTop:'20px'}}>
                  <h3 style={{marginBottom:'12px',fontFamily:'var(--font-head)',fontSize:'1.3rem',letterSpacing:'1px'}}>
                    RATE YOUR <span style={{color:'var(--teal)'}}>CARRIER</span>
                  </h3>
                  {reviewSubmitted ? (
                    <div style={{textAlign:'center',padding:'16px 12px',background:'var(--teal-dim)',border:'1px solid rgba(62,207,160,0.3)',borderRadius:'var(--radius-md)',color:'var(--teal)',marginBottom:'12px'}}>
                      <div style={{fontWeight:700,fontSize:'1rem',marginBottom:'8px'}}>✅ Review submitted — thank you!</div>
                      <div style={{fontSize:'0.85rem',opacity:0.85,marginBottom:'12px'}}>
                        Redirecting to your dashboard in {reviewRedirectCount}s…
                      </div>
                      <button className="btn btn-primary btn-sm" onClick={handleReset}>
                        Back to Dashboard
                      </button>
                    </div>
                  ) : (
                    <>
                      {!jobMarkedComplete && (
                        <p style={{marginBottom:'12px',fontSize:'0.88rem',color:'var(--teal)',background:'var(--teal-dim)',padding:'10px 14px',borderRadius:'var(--radius-sm)',border:'1px solid rgba(62,207,160,0.25)'}}>
                          Please mark the job as complete before leaving a review.
                        </p>
                      )}
                      {jobMarkedComplete && awaitingPayment && (
                        <p style={{marginBottom:'12px',fontSize:'0.88rem',color:'var(--teal)',background:'var(--teal-dim)',padding:'10px 14px',borderRadius:'var(--radius-sm)',border:'1px solid rgba(62,207,160,0.25)'}}>
                          Please pay above before leaving a review.
                        </p>
                      )}
                      <div className="star-rating" style={{opacity: reviewEnabled ? 1 : 0.4, pointerEvents: reviewEnabled ? 'auto' : 'none'}}>
                        {[1,2,3,4,5].map(n => (
                          <span
                            key={n}
                            className={`star-btn ${n <= (starHover || starRating) ? 'active' : ''}`}
                            onClick={() => setStarRating(n)}
                            onMouseEnter={() => setStarHover(n)}
                            onMouseLeave={() => setStarHover(0)}
                          >★</span>
                        ))}
                      </div>
                      <input
                        className="form-input"
                        style={{marginTop:'12px', opacity: reviewEnabled ? 1 : 0.4}}
                        placeholder="Leave a review (optional)"
                        value={reviewText}
                        onChange={e => setReviewText(e.target.value)}
                        disabled={!reviewEnabled}
                      />
                      <button
                        className="btn btn-primary btn-full"
                        style={{marginTop:'12px'}}
                        onClick={handleSubmitReview}
                        disabled={!starRating || submittingReview || !acceptedBid || !reviewEnabled}
                      >
                        {submittingReview ? 'SUBMITTING...' : 'SUBMIT REVIEW'}
                      </button>
                    </>
                  )}
                  <button
                    className="btn btn-secondary btn-full"
                    style={{marginTop:'10px', opacity: reviewSubmitted ? 1 : 0.45, cursor: reviewSubmitted ? 'pointer' : 'not-allowed'}}
                    disabled={!reviewSubmitted}
                    onClick={handleReset}
                  >
                    POST ANOTHER JOB
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* SIDEBAR */}
          <div className="dash-sidebar">
            <div className="card">
              <h3 className="card-title-sm">MY <span>JOBS</span></h3>
              {loadingJobs && <p className="text-muted" style={{fontSize:'0.85rem'}}>Loading...</p>}
              {!loadingJobs && myJobs.length === 0 && <p className="text-muted" style={{fontSize:'0.85rem'}}>No jobs yet.</p>}
              {myJobs.map(job => (
                <div key={job.id} className="sidebar-job">
                  <div className="sj-top">
                    <span className="sj-type">{job.serviceType}</span>
                    <span className={`badge ${job.status === 'Pending' ? 'badge-amber' : job.status === 'Completed' ? 'badge-teal' : job.status === 'Cancelled' ? 'badge-fire' : 'badge-muted'}`}>{job.status}</span>
                  </div>
                  <div className="sj-loc">{job.pickupAddress || job.pickup || job.pickupArea || '—'}</div>
                  <div className="sj-date">{job.date}</div>
                </div>
              ))}
            </div>
            <div className="card" style={{marginTop:'14px'}}>
              <h3 className="card-title-sm">YOUR <span>PROTECTION</span></h3>
              <div className="protection-list">
                <div className="prot-item">✅ Waste carriers are checked against the public register before they&apos;re approved. <RegisterCheck /></div>
                <div className="prot-item">📋 Waste transfer notes are a record, not a guarantee</div>
                <div className="prot-item">🛡️ Your duty of care stays with you</div>
                <div className="prot-item">🚫 Fly-tipping can get a carrier removed</div>
                <div className="prot-item">⭐ Reviews come from completed jobs</div>
              </div>
            </div>
            <div className="card" style={{marginTop:'14px'}}>
              <h3 className="card-title-sm">ECO <span>LEADERBOARD</span></h3>
              {leaderboard.length === 0
                ? <p className="text-muted" style={{fontSize:'0.82rem'}}>Loading...</p>
                : leaderboard.slice(0, 5).map((c, i) => (
                  <Link key={c.id} to={`/carrier/${c.id}`} style={{textDecoration:'none',display:'block',color:'inherit'}}>
                  <div className="sb-lb-row">
                    <div className={`sb-lb-rank r${i < 3 ? i + 1 : ''}`}>{i + 1}</div>
                    <div className="sb-lb-avatar">{c.name?.charAt(0) || '?'}</div>
                    <div className="sb-lb-info">
                      <div className="sb-lb-name">
                        {c.name}
                        {c.verified && <span className="sb-lb-tick">✅</span>}
                      </div>
                      <div className="sb-lb-meta">
                        {c.credits || 0} pts
                      </div>
                    </div>
                  </div>
                  </Link>
                ))
              }
            </div>
          </div>
        </div>
      </div>
      {/* HISTORY WTN MODAL */}
      {historyWtnModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setHistoryWtnModal(null)}>
          <div className="modal-box">
            <WasteTransferNote
              job={{
                pickupAddress: historyWtnModal.wtn.pickupAddress,
                dropoffAddress: historyWtnModal.wtn.dropoffAddress,
                dropoff: historyWtnModal.wtn.dropoff,
                location: historyWtnModal.wtn.location,
                description: historyWtnModal.wtn.description,
                serviceType: historyWtnModal.wtn.serviceType,
              }}
              carrier={{ providerName: historyWtnModal.wtn.carrierName, licenceNo: historyWtnModal.wtn.licenceNo, ecoRate: historyWtnModal.wtn.ecoRate }}
              wtnRef={historyWtnModal.wtn.ref}
            />
            <button className="btn btn-secondary btn-full" style={{marginTop:'12px'}} onClick={() => setHistoryWtnModal(null)}>
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerDashboard;
